/* eslint-disable react-hooks/rules-of-hooks */
import BigNumber from 'bignumber.js';
import moment from 'moment';
import { number, shape, string } from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Table } from 'semantic-ui-react';
import { CurrencyLabel } from 'Modules/Currency';
import { genMedicineInstruction } from 'Helpers/utils';
import { PHARMACY_SOURCE_OTTER, PHARMACY_SOURCE_LIFEPACK } from 'Constants/consults';
import PrivateImage from 'Modules/PrivateImage/PrivateImage';

import styles from './PrescriptionPreview.less';

const getLicenseNo = profession => {
  return `License No: ${profession?.licenseNo ?? '---'}`;
};

const subTotalValue = medicines =>
  medicines.reduce((acc, cur) => acc + +cur.quantity * +cur.unitPrice, 0).toFixed(2);

const subTotalValueStored = medicines =>
  medicines
    .reduce(
      (acc, cur) => acc + (cur.mode === 'add' ? 0 : +cur.quantity * cur.description.unitPrice),
      0,
    )
    .toFixed(2);

const renderMedicines = (medicines, pharmacy) => {
  const { hasStoredMedicines, source } = pharmacy;
  const isMedicineFromStorage =
    hasStoredMedicines || [PHARMACY_SOURCE_OTTER, PHARMACY_SOURCE_LIFEPACK].includes(source);

  if (isMedicineFromStorage) {
    return medicines.map(medicine => {
      const price = (+medicine.description.unitPrice * +medicine.quantity).toFixed(2);
      const instruction = genMedicineInstruction(medicine, true);
      return (
        <Table.Row key={medicine.id}>
          <Table.Cell width={10}>
            <div>{medicine.description.name}</div>
            <div className={styles.instruction}>
              {!medicine.useCustomInstruction ? (
                <span>{instruction}</span>
              ) : (
                <span>{medicine.note}</span>
              )}
            </div>
          </Table.Cell>
          <Table.Cell width={3} className={styles.cellTextRight}>
            {medicine.quantity}
          </Table.Cell>
          <Table.Cell width={3} className={styles.cellTextRight}>
            <CurrencyLabel value={medicine.description.unitPrice || 0} />
          </Table.Cell>
          <Table.Cell width={3} className={styles.cellTextRight}>
            <CurrencyLabel value={price || 0} />
          </Table.Cell>
        </Table.Row>
      );
    });
  }

  return medicines.map(medicine => (
    <Table.Row key={medicine.id}>
      <Table.Cell width={10}>{medicine.description}</Table.Cell>
      <Table.Cell width={3} className={styles.cellTextCenter}>
        {medicine.quantity}
      </Table.Cell>
      <Table.Cell width={3} className={styles.cellTextRight}>
        <CurrencyLabel value={medicine.unitPrice || 0} />
      </Table.Cell>
      <Table.Cell width={3} className={styles.cellTextRight}>
        <CurrencyLabel
          value={+new BigNumber(medicine.unitPrice || 0).multipliedBy(medicine.quantity || 0) || 0}
        />
      </Table.Cell>
    </Table.Row>
  ));
};

class PrescriptionPreview extends Component {
  render() {
    const {
      values: { note, medicines, deliveryFee },
    } = this.props;
    const {
      practiceCost,
      currencyCode,
      signatureUrl,
      conversation: {
        latestConsult: {
          forPatient: { fullName, nric, dateOfBirth },
        },
      },
      doctorInfo: { professions = [], title = '', name = '' },
      pharmacy,
    } = this.props;

    const today = moment().format('MMM DD YYYY');
    const stringDOB = moment(dateOfBirth).format('DD/MM/YYYY');
    const pharmacySource = pharmacy.source;
    const isMedicineFromStorage =
      pharmacy.hasStoredMedicines ||
      [PHARMACY_SOURCE_OTTER, PHARMACY_SOURCE_LIFEPACK].includes(pharmacySource);

    let subTotal = 0;
    if (isMedicineFromStorage) {
      subTotal = +subTotalValueStored(medicines);
    } else {
      subTotal = +subTotalValue(medicines);
    }

    const getTotal = () => {
      // Not calculated for Lifepack
      // if (pharmacySource === PHARMACY_SOURCE_LIFEPACK) {
      //   return +subTotal.toFixed(2);
      // }

      return +(subTotal + (+deliveryFee || 0) + practiceCost).toFixed(2);
    };

    return (
      <div className={styles.preview}>
        <div className={styles.previewContent}>
          <div className={styles.previewBody}>
            <h3 className={styles.title}>PRESCRIPTION</h3>
            <div className={styles.pationInfo}>
              <div className={styles.nameAge}>
                <div className={styles.pationName}>
                  <span className={styles.label}>For: </span>
                  <strong>{fullName}</strong>
                </div>
                <div className={styles.patientAge}>
                  <span className={styles.label}>Date of birth: </span>
                  <strong>{stringDOB}</strong>
                </div>
              </div>
              <div className={styles.nric}>
                <span className={styles.label}>NRIC/Passport Number: </span>
                <strong>{nric}</strong>
              </div>
              <div>
                <span className={styles.label}>Date: </span>
                <strong>{today}</strong>
              </div>
            </div>
            <div className={styles.listMedicines}>
              <Table celled>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell rowSpan="6" textAlign="left">
                      Medicine
                    </Table.HeaderCell>
                    <Table.HeaderCell rowSpan="6" textAlign="center">
                      Qty
                    </Table.HeaderCell>
                    <Table.HeaderCell rowSpan="6" textAlign="center">
                      Unit price
                    </Table.HeaderCell>
                    <Table.HeaderCell rowSpan="6" textAlign="center">
                      {`Amount (${currencyCode})`}
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>{renderMedicines(medicines, pharmacy)}</Table.Body>
              </Table>
              <div className={styles.price}>
                <div className={styles.empty} />
                <div className={styles.rowsPrice}>
                  <div className={styles.rowPrice}>
                    <div className={styles.textRow}>Subtotal: </div>
                    <div className={styles.valueRow}>
                      <CurrencyLabel value={subTotal} />
                    </div>
                  </div>

                  {!!practiceCost && (
                    <div className={styles.rowPrice}>
                      <div className={styles.textRow}>Practice Cost: </div>
                      <div className={styles.valueRow}>
                        <CurrencyLabel value={practiceCost} />
                      </div>
                    </div>
                  )}

                  {pharmacySource !== PHARMACY_SOURCE_LIFEPACK && (
                    <div className={styles.rowPrice}>
                      <div className={styles.textRow}>Delivery fee: </div>
                      <div className={styles.valueRow}>
                        <CurrencyLabel value={deliveryFee} />
                      </div>
                    </div>
                  )}

                  <hr className={styles.divider} />
                  <div className={`${styles.rowPrice} ${styles.totalRowBold}`}>
                    <div className={styles.textRow}>Total: </div>
                    <div className={styles.valueRow}>
                      <CurrencyLabel value={getTotal()} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.tip}>
              <div>Tax included where applicable</div>
            </div>
            <div className={styles.note}>
              <span className={styles.lableNote}>IMPORTANT NOTE: </span>
              <span className={styles.contentNote}>{note ? ` ${note}` : 'N/A'}</span>
            </div>
          </div>
          <div className={styles.previewFooter}>
            <div className={styles.pf}>
              <div className={styles.signature}>
                {signatureUrl !== null && (
                  <PrivateImage
                    src={signatureUrl}
                    alt="signature"
                    className={styles.imageSignature}
                    style={{ maxWidth: 100 }}
                  />
                )}
              </div>
              <div className={styles.doctorName}>
                <div>{name}</div>
              </div>
              <div className={styles.doctorTitle}>{title}</div>
              {professions?.length > 0 && (
                <div className={styles.profession}>
                  <div>{getLicenseNo(professions?.[0])}</div>
                  <div>{professions?.[0]?.name}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

PrescriptionPreview.propTypes = {
  conversation: shape({}).isRequired,
  doctorInfo: shape({}).isRequired,
  pharmacy: shape({}).isRequired,
  currencyCode: string.isRequired,
  practiceCost: number,
  values: shape({}).isRequired,
  deliveryFee: shape({}).isRequired,
  signatureUrl: string,
};

PrescriptionPreview.defaultProps = {
  signatureUrl: null,
  practiceCost: 0,
};

const mapStateToProps = state => ({
  certification: state.webChat.emc.certification,
  frequencies: state.webChat.prescription.prescriptionConfig.frequencies,
});

export default connect(mapStateToProps)(PrescriptionPreview);
